/**
 * Shared decoded image cache for canvas draw paths and boot preload.
 * One URL → one Image instance; inflight requests are deduplicated.
 */
var urlCache = Object.create(null);
var inflight = Object.create(null);

var DEFAULT_TIMEOUT_MS = 12000;

/**
 * @param {HTMLImageElement|null|undefined} img
 * @returns {boolean}
 */
export function isImageReady(img) {
  return !!(img && img.complete && img.naturalWidth > 0);
}

/**
 * @param {string} url
 * @returns {HTMLImageElement|null}
 */
export function getCachedImage(url) {
  if (!url) return null;
  var img = urlCache[url];
  return isImageReady(img) ? img : null;
}

/**
 * Register an already-drawn image source (a procedural canvas marked image-like) under `url`
 * so `getCachedImage(url)` and `preloadImageUrl(url)` return it without a network load.
 * @param {string} url
 * @param {HTMLImageElement|HTMLCanvasElement} source
 */
export function registerReadyImage(url, source) {
  if (!url || !isImageReady(source)) return;
  urlCache[url] = source;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isImageCached(url) {
  return !!getCachedImage(url);
}

/**
 * @param {string} url
 * @param {number} [timeoutMs]
 * @returns {Promise<HTMLImageElement|null>}
 */
export function preloadImageUrl(url, timeoutMs) {
  if (typeof window === "undefined" || !url) return Promise.resolve(null);

  var cached = getCachedImage(url);
  if (cached) return Promise.resolve(cached);

  if (inflight[url]) return inflight[url];

  var limit = timeoutMs != null ? timeoutMs : DEFAULT_TIMEOUT_MS;
  inflight[url] = new Promise(function (resolve) {
    var img = new Image();
    var settled = false;
    function finish(result) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      delete inflight[url];
      if (isImageReady(result)) {
        urlCache[url] = result;
        resolve(result);
      } else {
        resolve(null);
      }
    }
    img.onload = function () {
      function done() {
        finish(img);
      }
      if (img.decode && typeof img.decode === "function") {
        img.decode().then(done, done);
      } else {
        done();
      }
    };
    img.onerror = function () { finish(null); };
    var timer = setTimeout(function () { finish(null); }, limit);
    img.src = url;
    if (img.complete && img.naturalWidth > 0) finish(img);
  });

  return inflight[url];
}

/**
 * @param {string[]} urls
 * @param {number} [timeoutMs]
 * @returns {Promise<(HTMLImageElement|null)[]>}
 */
export function preloadImageUrls(urls, timeoutMs) {
  var list = urls || [];
  return Promise.all(list.map(function (url) {
    return preloadImageUrl(url, timeoutMs);
  }));
}

/** @internal Test helper */
export function __resetImageCacheForTests() {
  urlCache = Object.create(null);
  inflight = Object.create(null);
}